import { Component, HostListener, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ApiCallService } from '@pkg-services/callApi/api-call.service';
import { createUserCookies } from '@pkg-services/cookies/Cookies';
import { AdminService } from '@pkg-services/admin/admin.service';
import { SimpleButtonComponent } from '@pkg-components/button/simple-button/simple-button.component';
import { NotificationComponent } from '@pkg-components/notification/notification/notification.component';
import { FormGeneratorComponent } from '@pkg-components/forms/form-generator/form-generator.component';
import { FormGenerator } from '@pkg-framework/forms/FormGenerator';
import { ThemeService } from '@pkg-services/theme/theme.service';
import { NotifyModalService } from '@pkg-services/notification/NotifyModal.service';
import type { AdminLoginResponse } from '@pkg-framework/models/admin';
import { RouteAdminLogin } from '@pkg-framework/models/admin';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterLink, SimpleButtonComponent, NotificationComponent, FormGeneratorComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  loaderLogin = false;
  formValues = { email: '', password: '' };
  loginFormConfig: FormGenerator;

  @HostListener('document:keypress', ['$event']) onDown(event: KeyboardEvent) {
    if (event.key === 'Enter') this.login();
  }

  constructor(
    private router: Router,
    private apiService: ApiCallService,
    private adminService: AdminService,
    private notification: NotifyModalService,
    private themeService: ThemeService,
    private ngZone: NgZone
  ) {
    this.initializeFormConfig();
    if (this.apiService.getToken()) {
      this.router.navigateByUrl('/admin').then();
    }
  }

  private initializeFormConfig(): void {
    this.loginFormConfig = new FormGenerator([
      { formType: 'input', field: 'email', label: 'Email', type: 'email', required: true },
      { formType: 'input', field: 'password', label: 'Mot de passe', type: 'password', required: true }
    ]);
  }

  login() {
    this.loaderLogin = true;
    this.loginFormConfig.updateFormData(this.formValues);
    if (!this.loginFormConfig.validateAllFields()) {
      this.loaderLogin = false;
      return;
    }
    if (!this.formValues.email?.trim() || !this.formValues.password?.trim()) {
      this.notification.warning('Champs requis', ['Email et mot de passe sont obligatoires']);
      this.loaderLogin = false;
      return;
    }
    this.apiService.postPublic(RouteAdminLogin, this.formValues).subscribe({
      next: (response: AdminLoginResponse) => {
        this.loaderLogin = false;
        const payload = response?.data;
        const token = payload?.token;
        const admin = payload?.admin;
        if (!token || !admin) {
          this.notification.warning('Réponse invalide', ['Le serveur n\'a pas renvoyé les données attendues.']);
          return;
        }
        createUserCookies(
          token,
          admin.name ?? '',
          admin.sub_name ?? '',
          admin.uuid ?? '',
          admin.email ?? this.formValues.email ?? ''
        );
        this.adminService.setCurrentAdmin(admin);
        this.themeService.applyTheme('light');
        this.ngZone.run(() => {
          this.router.navigateByUrl('/admin').then();
        });
      },
      error: (err: { error?: { message?: string; errors?: string[] | Record<string, string[]> } }) => {
        this.loaderLogin = false;
        const body = err?.error;
        let msg = body?.message ?? 'Connexion impossible.';
        const raw = body?.errors;
        if (Array.isArray(raw) && raw.length) {
          msg = String(raw[0]);
        } else if (raw && typeof raw === 'object') {
          const first = Object.values(raw).flat()[0];
          if (first) msg = String(first);
        }
        const pending = /invitation|en attente/i.test(msg);
        this.notification.warning(
          pending ? 'Compte non activé' : 'Connexion',
          pending
            ? [msg, 'Utilisez « Mot de passe oublié ? » pour recevoir un nouveau lien d’invitation.']
            : [msg]
        );
      }
    });
  }
}
